import * as React from "react";
import { useMemo, useCallback } from 'react';
import { ProgramClassMeeting } from "../../interfaces/program";
import { getTimezoneAbbreviation } from "../../../shared/api/time";

interface MeetingTimeSummaryProps {
    timezone: string;
    meetings: Array<ProgramClassMeeting>;
}

const MAXITEMS = 3;

// This takes the meetings times and consolidates the list so it shows the days of
// the week and times. If the variety is too much (e.g. over 3) then it shows a generic
// messages
const MeetingTimeSummary = (
    { timezone, meetings }: MeetingTimeSummaryProps,
) => {
    const tzAbbrev = getTimezoneAbbreviation(timezone);

    const dayFormatter = useMemo(() => {
        return new Intl.DateTimeFormat("en-US", {
            weekday: "short",
            timeZone: timezone,
        });
    }, [timezone]);

    const timeFormatter = useMemo(() => {
        return new Intl.DateTimeFormat("en-US", {
            hour: "numeric",
            minute: "numeric",
            hour12: true,
            timeZone: timezone,
        });
    }, [timezone]);

    const getFormattedDateRange = useCallback((
        startMs: number,
        endMs: number,
    ): string => {
        const startDate = new Date(startMs);
        const endDate = new Date(endMs);
        const day = dayFormatter.format(startDate);
        const startTime = timeFormatter.format(startDate);
        const endTime = timeFormatter.format(endDate);

        // Use the memoized tzAbbrev from the outer scope
        if (startMs === endMs) {
            return `${day}: ${startTime} ${tzAbbrev}`;
        } else {
            return `${day}: ${startTime} - ${endTime} ${tzAbbrev}`;
        }
    }, [dayFormatter, timeFormatter, tzAbbrev]); // Depend on memoized formatters and tzAbbrev

    const uniqueTransformed = new Set();
    const result: Array<string> = [];

    for (const mtg of meetings) {
        const transformed = getFormattedDateRange(
            mtg.scheduleDateMs,
            mtg.scheduleEndDateMs,
        );

        if (!uniqueTransformed.has(transformed)) {
            uniqueTransformed.add(transformed);
            result.push(transformed);

            if (result.length > MAXITEMS) {
                return <div>"Various times. Consult schedule."</div>;
            }
        }
    }

    return (
        <div>
            {result.map((c, idx) => <div key={idx}>{c}</div>)}
        </div>
    );
};

export default React.memo(MeetingTimeSummary);
